import React, { useEffect, useState } from "react";
import { Heart, X, HandCoins } from "lucide-react";
import { toast } from "sonner";
import client, { apiError } from "../lib/api";
import { Crest, Card, Pill, SectionTitle } from "./common";

export function SupportClubs({ presetClub }) {
  const [campaigns, setCampaigns] = useState([]);
  const [settings, setSettings] = useState({ donation_fee_pct: 15, payment_provider: "PayPal (pending)" });
  const [open, setOpen] = useState(null); // club object
  const load = () => client.get("/support/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => {
    load();
    client.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const list = presetClub ? campaigns.filter((c) => c.club_slug === presetClub) : campaigns;
  const active = list.filter((c) => c.active);
  const show = active.length ? active : list;

  return (
    <div data-testid="support-clubs">
      {!presetClub && <SectionTitle kicker="Diaspora support · USD">Back your club</SectionTitle>}
      <div className="p-5 mb-4 rounded-sm text-cream" style={{ backgroundColor: "#E40119" }}>
        <div className="flex items-start gap-3">
          <HandCoins className="text-gold shrink-0" />
          <p className="text-sm text-cream/90">
            Wherever you are in the world, support your club's growth. {100 - settings.donation_fee_pct}% goes
            straight to the club; {settings.donation_fee_pct}% keeps the platform running.
            <b className="text-gold"> Donations open in November 2026</b> — take a look around now.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {show.map((c) => (
          <Card key={c.club_slug} className="p-4" data-testid={`support-${c.club_slug}`}>
            <div className="flex items-center gap-3 mb-2">
              <Crest slug={c.club_slug} name={c.club_name} size={40} url={c.badge} />
              <div className="flex-1">
                <div className="font-head font-bold text-green leading-tight">{c.club_name}</div>
                {c.active && <Pill tone="gold">Appeal open</Pill>}
              </div>
            </div>
            {c.message && <p className="text-xs text-ink/60 mb-2 line-clamp-2">{c.message}</p>}
            <div className="text-sm mb-3">Raised: <b className="text-green tabular">${c.raised.toFixed(2)}</b>{c.goal_usd ? <span className="text-ink/50"> / ${c.goal_usd}</span> : null}</div>
            <button onClick={() => setOpen(c)} className="w-full py-2 bg-gold text-ink font-bold rounded-sm hover:bg-green hover:text-cream transition-colors flex items-center justify-center gap-1" data-testid={`donate-btn-${c.club_slug}`}>
              <Heart size={15} /> Donate
            </button>
          </Card>
        ))}
      </div>
      {open && <DonateModal club={open} feePct={settings.donation_fee_pct} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function DonateModal({ club, feePct, onClose, onDone }) {
  const [amount, setAmount] = useState(10);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [anon, setAnon] = useState(false);
  const [pub, setPub] = useState(true);
  const fee = (Number(amount) || 0) * feePct / 100;
  const net = (Number(amount) || 0) - fee;

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={onClose} data-testid="donate-modal">
      <div className="bg-white rounded-sm w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Crest slug={club.club_slug} name={club.club_name} size={36} url={club.badge} /><h3 className="font-head font-black text-green">Support {club.club_name}</h3></div>
          <button onClick={onClose} className="text-ink/40 hover:text-live"><X /></button>
        </div>
        <label className="text-xs uppercase tracking-wider text-ink/50 font-bold">Amount (USD)</label>
        <div className="flex gap-2 my-2">
          {[5, 10, 25, 50].map((a) => <button key={a} onClick={() => setAmount(a)} className={`flex-1 py-2 rounded-sm font-bold text-sm ${Number(amount) === a ? "bg-green text-cream" : "bg-cream border border-pslborder"}`}>${a}</button>)}
        </div>
        <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border border-pslborder rounded-sm p-2.5 text-sm mb-3 focus:outline-none focus:border-gold" data-testid="donate-amount" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="w-full border border-pslborder rounded-sm p-2.5 text-sm mb-2 focus:outline-none focus:border-gold" data-testid="donate-name" />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message of support (optional)" rows={2} className="w-full border border-pslborder rounded-sm p-2.5 text-sm mb-2 focus:outline-none focus:border-gold" />
        <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} /> Donate anonymously</label>
        <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} data-testid="donate-public" /> Show me on the club's supporters wall</label>
        <div className="bg-cream rounded-sm p-3 text-sm mb-4">
          <div className="flex justify-between"><span className="text-ink/60">To {club.club_name}</span><b className="tabular text-green">${net.toFixed(2)}</b></div>
          <div className="flex justify-between"><span className="text-ink/60">Platform fee ({feePct}%)</span><span className="tabular">${fee.toFixed(2)}</span></div>
        </div>
        <button disabled className="w-full py-3 bg-cream text-ink/50 font-bold rounded-sm border border-pslborder cursor-not-allowed" data-testid="donate-submit">
          Donations open November 2026
        </button>
        <p className="text-[11px] text-ink/50 mt-2 text-center">We're not accepting donations just yet — collections open in <b>November 2026</b>. Thanks for backing {club.club_name}!</p>
      </div>
    </div>
  );
}
